import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { userManagementService } from '../../../services/userManagementService';
import { authService } from '../../../services/authService';

const UserTroubleshooting = () => {
  const [searchEmail, setSearchEmail] = useState('');
  const [foundUsers, setFoundUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const searchUserByEmail = async () => {
    if (!searchEmail?.trim()) {
      setError('Please enter an email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Get all users and filter by email
      const allUsers = await userManagementService?.getAllUsers();
      const matchingUsers = allUsers?.filter(user => 
        user?.email?.toLowerCase()?.includes(searchEmail?.toLowerCase())
      ) || [];

      setFoundUsers(matchingUsers);

      if (matchingUsers?.length === 0) {
        setError(`No user found with email "${searchEmail}". Please check the Supabase database.`);
      }
    } catch (error) {
      console.error('Error searching user:', error);
      setError(`Error searching for user: ${error?.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const activateUser = async (userId) => {
    try {
      await userManagementService?.updateUserStatus(userId, 'active');
      // Refresh search results
      await searchUserByEmail();
      alert('User successfully activated');
    } catch (error) {
      console.error('Error activating user:', error);
      alert(`Error activating user: ${error?.message}`);
    }
  };

  const resetUserPassword = async (email) => {
    try {
      await authService?.resetPassword(email);
      alert(`Password reset email sent to ${email}`);
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(`Error resetting password: ${error?.message}`);
    }
  };

  return (
    <div className="mt-8 space-y-6">
      <div className="flex items-center space-x-2">
        <Icon name="Search" size={20} className="text-primary" />
        <h2 className="text-xl font-semibold text-foreground">
          User Troubleshooting
        </h2>
      </div>

      {/* Search Section */}
      <div className="p-6 bg-card rounded-lg border border-border">
        <h3 className="text-lg font-medium text-foreground mb-4">
          Search User
        </h3>
        
        <div className="flex space-x-4">
          <div className="flex-1">
            <Input
              label="Email Address"
              type="email"
              placeholder="user@example.com"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e?.target?.value)}
              disabled={isLoading}
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={searchUserByEmail}
              loading={isLoading}
              disabled={isLoading || !searchEmail?.trim()}
              iconName="Search"
            >
              Search
            </Button>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-error/10 border border-error/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
              <p className="text-sm text-error">{error}</p>
            </div>
          </div>
        )}
      </div>

      {/* Search Results */}
      {foundUsers?.length > 0 && (
        <div className="p-6 bg-card rounded-lg border border-border">
          <h3 className="text-lg font-medium text-foreground mb-4">
            Search Results ({foundUsers?.length})
          </h3>
          
          <div className="space-y-4">
            {foundUsers?.map((user) => (
              <div key={user?.id} className="p-4 border border-border rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon 
                        name={user?.status === 'active' ? 'CheckCircle' : 'XCircle'} 
                        size={16} 
                        className={user?.status === 'active' ? 'text-green-500' : 'text-error'}
                      />
                      <h4 className="font-medium text-foreground">
                        {user?.full_name || 'Unknown'}
                      </h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user?.status === 'active' ?'bg-green-100 text-green-700' :'bg-red-100 text-red-700'
                      }`}>
                        {user?.status}
                      </span>
                    </div>
                    
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div><strong>Email:</strong> {user?.email}</div>
                      <div><strong>Role:</strong> {user?.role || 'user'}</div>
                      <div><strong>Created:</strong> {new Date(user?.created_at)?.toLocaleDateString()}</div>
                      <div><strong>Last Login:</strong> {
                        user?.last_login 
                          ? new Date(user?.last_login)?.toLocaleString()
                          : 'Never'
                      }</div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    {user?.status !== 'active' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => activateUser(user?.id)}
                        iconName="UserCheck"
                      >
                        Activate
                      </Button>
                    )}
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => resetUserPassword(user?.email)}
                      iconName="Key"
                    >
                      Reset Password
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* General Troubleshooting Tips */}
      <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-500 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-medium text-blue-900 mb-2">
              Common Issues & Solutions
            </h3>
            <div className="text-sm text-blue-800 space-y-2">
              <p><strong>User can't sign in:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Account doesn't exist in the Supabase database</li>
                <li>Email was not confirmed</li>
                <li>Account is inactive (status != 'active')</li>
                <li>Password is incorrect or forgotten</li>
                <li>Supabase project is paused</li>
              </ul>
              
              <div className="mt-4 p-3 bg-white rounded border">
                <p className="font-medium mb-2">Quick Actions:</p>
                <div className="space-y-2">
                  <p className="text-xs text-gray-600">
                    Use the search above to find users and activate accounts or reset passwords as needed.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-start space-x-3">
          <Icon name="Settings" size={20} className="text-gray-500 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              System Status
            </h3>
            <div className="text-sm text-gray-700 space-y-1">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-green-500" />
                <span>Profiles table configured</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-green-500" />
                <span>RLS policies enabled</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-green-500" />
                <span>Admin functions available</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-green-500" />
                <span>User management service active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserTroubleshooting;